/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { EntityLabel } from 'src/luis/projects/utterances/types/Utterance';
import { ModelType } from 'src/luis/types/ModelType';

/**
 * An internal type used to link composite children with their parents, orphan entities gets assigned to a null parent.
 */
type CompoundEntity = {
    /**
     * The parent composite entity.
     */
    parent: EntityLabel | null;

    /**
     * Children that corresponds to the parent entity.
     */
    children: EntityLabel[];
};

type OverlapResolver = (a: EntityLabel, b: EntityLabel) => boolean;

/**
 * Removes overlapping entities between labeled and non-machine learned entities. Machine learned entities are not displayed to the user
 * instead they are only used for predictions.
 *
 * @param labeledEntities Utterance labeled entities.
 * @param nonMachineLearnedEntities Array of non-machine learned entities
 * @returns Array of labeled and non-machine learned entities combined with no overlaps
 */
export const resolveOverlappingEntities = (labeledEntities: EntityLabel[], nonMachineLearnedEntities: EntityLabel[]): EntityLabel[] => {
    // eslint-disable-next-line prefer-const
    let { compositeEntities, nonCompositeEntities, nonMLEntities } = massageEntities(labeledEntities, nonMachineLearnedEntities);

    nonMLEntities = resolveOverlapsBetweenTwoEntities({
        mainEntities: nonCompositeEntities,
        secondaryEntities: nonMLEntities
    });

    const compoundEntities = constructCompoundEntities(compositeEntities, [...nonCompositeEntities, ...nonMLEntities]);

    return unwindCompoundEntities(compoundEntities);
};

/// ///////////////////////////////// PREPROCESS ////////////////////////////////////

/**
 * Massages the entities by doing the following steps:
 * 1) Segments the input entities into four buckets, composite, non-composite, ML and non-ML
 * 2) Sorts these entities by their length
 * 3) Sorts ML entities by their type according to the rules in the precedence map
 * 4) Removes the overlaps between the entities and themselves
 */
const massageEntities = (labeledEntities: EntityLabel[], predictedEntities: EntityLabel[]) => {
    let compositeEntities = labeledEntities.filter(({ entity: { typeId } }) => typeId === ModelType.Composite).sort(sortByLength);
    let nonCompositeEntities = labeledEntities.filter(({ entity: { typeId } }) => typeId !== ModelType.Composite).sort(sortByLength);
    let nonMLEntities = predictedEntities
        .filter(({ entity: { typeId } }) => !isMachineLearned(typeId))
        .sort(sortByLength)
        .sort(sortByType);
    let MLEntities = predictedEntities.filter(({ entity: { typeId } }) => isMachineLearned(typeId)).sort(sortByLength);

    compositeEntities = removeOverlaps(compositeEntities);
    nonCompositeEntities = removeOverlaps(nonCompositeEntities);
    nonMLEntities = removeOverlaps(nonMLEntities);
    MLEntities = removeOverlaps(MLEntities);

    return { compositeEntities, nonCompositeEntities, nonMLEntities, MLEntities };
};

/// ///////////////////////////////// MERGE COMPOSITE AND NON-COMPOSITE ////////////////////////////////////

/**
 * Assigns composite entities to their children. Orphan entities gets added to a null parent.
 */
const constructCompoundEntities = (compositeEntities: EntityLabel[], resolvedEntities: EntityLabel[]): CompoundEntity[] => {
    const compositeEntitiesObj = compositeEntities.map(entity => {
        const compositeEntityChildren = getCompositeEntityChildren(entity, resolvedEntities);

        compositeEntityChildren.forEach(child => {
            const index = resolvedEntities.indexOf(child);
            if (index > -1) {
                resolvedEntities.splice(index, 1);
            }
        });

        return { parent: entity, children: compositeEntityChildren };
    });

    resolvedEntities = resolveOverlapsBetweenTwoEntities({
        mainEntities: compositeEntities,
        resolvers: [aPartiallyEclipsesB, bEclipsesA],
        secondaryEntities: resolvedEntities
    });

    return [...compositeEntitiesObj, { parent: null, children: resolvedEntities }];
};

const getCompositeEntityChildren = (parentEntity: EntityLabel, labeledEntities: EntityLabel[]): EntityLabel[] =>
    labeledEntities.filter(
        entity => parentEntity.startTokenIndex <= entity.startTokenIndex && parentEntity.endTokenIndex >= entity.endTokenIndex
    );

/// ///////////////////////////////// HELPERS ////////////////////////////////////

/**
 * Removes the entities that overlap from the secondaryEntities array with mainEntities array, according to the given resolvers.
 */
const resolveOverlapsBetweenTwoEntities = ({
    mainEntities,
    secondaryEntities = mainEntities,
    resolvers = [aEclipsesB, bEclipsesA]
}: {
    mainEntities: EntityLabel[];
    secondaryEntities?: EntityLabel[];
    resolvers?: OverlapResolver[];
}): EntityLabel[] => {
    secondaryEntities = secondaryEntities.slice();
    for (const entityA of mainEntities) {
        for (let j = secondaryEntities.length - 1; j >= 0; j -= 1) {
            const entityB = secondaryEntities[j];
            if (resolvers.reduce((acc, resolver) => acc || resolver(entityA, entityB), false)) {
                secondaryEntities.splice(j, 1);
            }
        }
    }

    return secondaryEntities;
};

/**
 * Sorts entities by their length.
 */
const sortByLength = (a: EntityLabel, b: EntityLabel): 1 | -1 =>
    a.endTokenIndex - a.startTokenIndex < b.endTokenIndex - b.startTokenIndex ? 1 : -1;

/**
 * Sorts entities by their type based on the precedence map.
 */
const sortByType = (a: EntityLabel, b: EntityLabel): 1 | -1 => {
    const precedenceMap = {
        [ModelType.Composite]: 3,
        [ModelType.Simple]: 3,
        [ModelType.List]: 2,
        [ModelType.Hierarchical]: 2,
        [ModelType.PatternAny]: 2,
        [ModelType.Regex]: 2,
        [ModelType.Prebuilt]: 0
    };

    return (<any>precedenceMap)[a.entity.typeId] <= (<any>precedenceMap)[b.entity.typeId] ? 1 : -1;
};

const aEclipsesB: OverlapResolver = (a, b) => a.startTokenIndex <= b.startTokenIndex && a.endTokenIndex >= b.endTokenIndex;
const bEclipsesA: OverlapResolver = (a, b) => a.startTokenIndex >= b.startTokenIndex && a.startTokenIndex <= b.endTokenIndex;

const aPartiallyEclipsesB: OverlapResolver = (a, b) =>
    (a.startTokenIndex > b.startTokenIndex && a.startTokenIndex < b.endTokenIndex && a.endTokenIndex >= b.endTokenIndex) ||
    (a.startTokenIndex <= b.startTokenIndex && a.endTokenIndex > b.startTokenIndex && a.endTokenIndex < b.endTokenIndex);

/**
 * Removes all the overlaps between the given entities array.
 */
const removeOverlaps = (entityLabels: EntityLabel[]): EntityLabel[] => {
    const utteranceEntitiesCopy = entityLabels.slice();

    for (let i = 0; i < utteranceEntitiesCopy.length; i += 1) {
        const longerEntity = utteranceEntitiesCopy[i];
        for (let j = i + 1; j < utteranceEntitiesCopy.length; j += 1) {
            const shorterEntity = utteranceEntitiesCopy[j];
            if ([aEclipsesB, bEclipsesA].reduce((acc, resolver) => acc || resolver(longerEntity, shorterEntity), false)) {
                utteranceEntitiesCopy.splice(j, 1);
                j -= 1;
            }
        }
    }

    return utteranceEntitiesCopy;
};

/**
 * Unwinds compound entities into a flat array of utterance entities.
 */
const unwindCompoundEntities = (compoundEntities: CompoundEntity[]): EntityLabel[] => {
    const compositeEntities = compoundEntities.map(({ parent }) => parent).filter(parent => parent !== null);
    const nonCompositeEntities = compoundEntities.find(({ parent }) => parent === null).children;

    return [...compositeEntities, ...nonCompositeEntities];
};
